// A tiny ordered registry of per-frame steps, grouped by named "slot" (a point in
// the render loop). Subsystems register their per-frame work into a slot from
// their OWN module instead of editing main's render loop; the loop just calls
// run(slot) at each point. This is the seam that lets new per-frame features land
// without touching the central loop (one of main.ts's biggest merge-conflict
// hot zones).
//
// Steps in a slot run in ascending `order` (ties keep registration order). The
// list is sorted once, at register time — run() is a plain ordered walk, so it
// stays allocation-free and is safe to call every frame. Register at init only;
// don't mutate the registry per frame.

export interface FrameLoop {
  register(slot: string, fn: () => void, order?: number): void;
  run(slot: string): void;
}

export function createFrameLoop(): FrameLoop {
  interface Step {
    fn: () => void;
    order: number;
    seq: number; // tiebreak: preserve registration order within equal `order`
  }
  const slots = new Map<string, Step[]>();
  let seq = 0;
  return {
    register(slot, fn, order = 0): void {
      const list = slots.get(slot);
      const step = { fn, order, seq: seq++ };
      if (list) {
        list.push(step);
        list.sort((a, b) => a.order - b.order || a.seq - b.seq);
      } else {
        slots.set(slot, [step]);
      }
    },
    run(slot): void {
      const list = slots.get(slot);
      if (!list) return; // no steps registered for this slot → no-op
      for (const s of list) s.fn();
    },
  };
}
